import React from "react";
import { useParams } from "react-router-dom";

const projectDetails = {
  "facial-recognition": {
    title: "Facial Recognition",
    description: `
Developed a high-accuracy facial recognition system using advanced CNN models, achieving 98.76% validation accuracy.
Integrated real-time camera feeds with a modular data processing pipeline for live face detection and recognition.
    `.trim(),
    points: [
      "Model Development: Trained custom CNNs using TensorFlow and Keras with data augmentation and early stopping.",
      "Face Detection: Utilized OpenCV, dlib, and face_recognition libraries for accurate landmark detection and encoding.",
      "System Integration: Built a modular architecture handling image capture, embedding generation, and identity matching.",
      "Deployment: Resolved complex dependency issues using CMake and Visual Studio Build Tools for smooth execution."
    ],
    conclusion: "Achieved real-time performance, making it ideal for security and authentication use cases.",
    image: `${process.env.PUBLIC_URL}/FR-HomePage.png`,
  },

  "facebook-post-ml-analysis": {
    title: "Facebook Post ML Analysis",
    description: `
  Performed data clustering and PCA on Facebook post engagement data from Thai fashion retailers to identify key consumer behavior patterns.
    `.trim(),
    points: [
      "Data Preprocessing: Cleaned and prepared 7,051 social media posts for clustering analysis.",
      "Modeling: Applied K-Means clustering and PCA to uncover seasonal engagement patterns and customer interest segments.",
      "Outlier Analysis: Identified statistical outliers for qualitative analysis and strategic business insights.",
      "Visualization: Generated clear and impactful visualizations using Matplotlib and Seaborn for business reporting."
    ],
    conclusion: "Provided actionable marketing insights to optimize campaign effectiveness and customer targeting.",
    image: `${process.env.PUBLIC_URL}/Facebook-Market.png`,
  },

  "boggle-algorithms": {
    title: "Boggle Algorithms",
    description: `
  Developed a highly optimized algorithm capable of finding over 68,000 valid words on a 16x16 Boggle board within one second.
    `.trim(),
    points: [
      "Algorithm Design: Combined advanced indexing techniques with depth-first search for optimal performance.",
      "Programming Languages: Implemented solutions using Rust, Haskell, and Elixir to compare runtime efficiencies.",
      "Optimization: Focused on minimizing search space and leveraging language-specific performance features.",
      "Performance: Achieved significant speed improvements, outperforming standard backtracking approaches."
    ],
    conclusion: "Demonstrated high-performance algorithm design for complex search problems.",
    image: `${process.env.PUBLIC_URL}/Boggle.png`,
  },

  "data-analysis-of-student-responses": {
    title: "Data Analysis of Student Responses",
    description: `
  Analyzed over 100 student responses to assess the impact of AI tools like ChatGPT on modern education.
    `.trim(),
    points: [
      "Data Cleaning: Processed raw survey data using Pandas for accurate and reliable analysis.",
      "Pattern Discovery: Uncovered hidden patterns and sentiment trends using advanced statistical methods.",
      "Visualization: Created complex and intuitive graphs in Python and Excel to illustrate key findings.",
      "Reporting: Presented clear conclusions on AI adoption trends and its perceived effectiveness in education."
    ],
    conclusion: "Provided actionable insights into how AI is shaping modern learning experiences.",
    image: `${process.env.PUBLIC_URL}/Stuent-Response.png`,
  },

  "image-generation": {
    title: "Image Generation",
    description: `
  Built an AI-based image generation system using Generative Adversarial Networks (GANs) to create realistic images.
    `.trim(),
    points: [
      "Model Development: Designed and trained GAN models using TensorFlow and Keras for high-fidelity image generation.",
      "Training: Applied advanced techniques such as feature matching and progressive growing to improve output quality.",
      "Optimization: Tuned hyperparameters and loss functions to stabilize GAN training and avoid mode collapse.",
      "Visualization: Produced visually compelling synthetic images, demonstrating creative AI capabilities."
    ],
    conclusion: "Successfully generated high-quality images, showcasing advanced AI creativity and modeling skills.",
    image: `${process.env.PUBLIC_URL}/Car.png`,
  },

  "paper-trading": {
    title: "Paper Trading Application",
    description: `
  Developed a Java-based GUI application for virtual stock trading, enabling users to simulate real-time market transactions.
    `.trim(),
    points: [
      "Application Design: Built using Java Swing with a user-friendly interface for seamless trading simulations.",
      "Database Integration: Connected to a remote Oracle database to store transaction history and portfolio data.",
      "Market Simulation: Implemented realistic stock price movements using historical data for effective practice trading.",
      "Cloud Experience: Gained hands-on experience with cloud-based data storage and remote query management."
    ],
    conclusion: "Provided a safe environment for users to practice stock trading strategies without financial risk.",
    image: `${process.env.PUBLIC_URL}/Paper-Trading.png`,
  },

  "gym-database": {
    title: "Gym Database Management System",
    description: `
  Designed and implemented a database management system to efficiently handle gym member records and subscriptions.
    `.trim(),
    points: [
      "Database Design: Created normalized relational schemas using SQL to ensure data integrity and efficiency.",
      "Query Development: Implemented complex SQL queries for membership tracking, attendance, and subscription analysis.",
      "User Interface: Developed a simple Java GUI for easy data entry and management by gym administrators.",
      "Reporting: Generated detailed member activity and subscription reports for business insights."
    ],
    conclusion: "Streamlined membership management and improved operational efficiency for gym administration.",
    image: `${process.env.PUBLIC_URL}/Gym-Database.png`,
  },

  "club-finance-app": {
    title: "Club Finance App",
    description: `
  Built a financial management application tailored for student clubs to handle budgets, expenses, and financial reporting.
    `.trim(),
    points: [
      "Application Development: Designed and implemented a user-friendly finance tracker using Python and SQLite.",
      "Budget Management: Allowed clubs to manage multiple budgets, track expenses, and generate financial summaries.",
      "Data Visualization: Included clear financial charts and reports to support informed decision-making.",
      "Security: Implemented basic authentication and data validation to ensure secure financial records."
    ],
    conclusion: "Enabled student clubs to maintain transparent and efficient financial management practices.",
    image: `${process.env.PUBLIC_URL}/Finance-App.png`,
  },
};

export default function ProjectDetails() {
  const { projectId } = useParams();
  const project = projectDetails[projectId];

  if (!project) {
    return (
      <div className="px-8 pt-[30vh] text-white min-h-screen bg-gray-900 text-center">
        <h1 className="text-4xl font-bold text-purple-400 mb-8">Sorry, Project Not Found</h1>
        <p className="text-gray-300 text-lg">Sorry, the project you’re looking for doesn’t exist.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row items-center justify-center gap-16 px-8 py-20 min-h-screen bg-gray-900 text-white"
         style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/BG-Project3.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
    >
    <img
      src={project.image}
      alt={project.title}
      className="w-full md:w-1/2 h-auto object-cover rounded-2xl shadow-lg"
    />
    <div className="max-w-2xl text-center md:text-left">
      <h1 className="text-4xl font-bold mb-6 text-purple-400">{project.title}</h1>
      
      <p className="text-gray-300 leading-relaxed text-lg mb-4">
        {project.description}
      </p>
      
      {project.points && (
        <ul className="text-gray-300 leading-relaxed text-lg list-disc list-inside space-y-2">
          {project.points.map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      )}
      <p className="text-gray-300 leading-relaxed text-lg mt-4">
        {project.conclusion}
      </p>
    </div>
  </div>
  );
}